/// ============================================================================
// ProjectInfoManage.jsx
// -----------------------------------------------------------------------------
// Composant modal de création/édition d’un projet
// Gère les champs du formulaire, les statuts et la synchro "Archived"
// ============================================================================
import React, { useState, useEffect } from "react";
import theme from "../../../styles/theme";
import '../../../styles/main.css';
import Input from "../../reusable/Forms/Input";
import TextArea from "../../reusable/Forms/TextArea";
import DropdownList from "../../reusable/Forms/DropdownList";
import Checkbox from "../../reusable/Forms/Checkbox";
import { PROJECT_STATUS} from "../../../base/constants";
import { PROJECTS_API } from "../../../config/api";
import{apiFetch} from "../../../config/apiFetch";
import ModalTemplate from "../../templates/ModalTemplate";

// ============================================================================
// Composant principal
// ============================================================================
const ProjectInfoManage = ({ isOpen, onClose, onSave, mode = "create", initialValues = null }) => {

  // --------------------------------------------------------------------------
  // 1. État initial du formulaire
  // --------------------------------------------------------------------------
  const initialState = {
    id: null,
    name: "",
    code: "",
    description: "",
    location: "",
    sector: "",
    status: "Active",
    currency: "EUR",
    archived: false,
    userId: null,
    accountId: null,
  };

  const [formData, setFormData] = useState(initialState);

  // --------------------------------------------------------------------------
  // 2. Initialisation selon le mode (création ou édition)
  // --------------------------------------------------------------------------
 useEffect(() => {
    if (!isOpen) return;
    setFormData(initialValues ? { ...initialState, ...initialValues } : initialState);
  }, [isOpen, initialValues]);

  // --------------------------------------------------------------------------
  // 3. Liaison automatique : archived = true si status === "Archived"
  // --------------------------------------------------------------------------
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      archived: prev.status === "Archived",
    }));
  }, [formData.status]);

  // --------------------------------------------------------------------------
  // 4. Gestion des changements dans les champs
  // --------------------------------------------------------------------------
  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({ ...formData, [field]: value });
  };

  // --------------------------------------------------------------------------
  // 5. Enregistrement (POST ou PUT selon le mode)
  // --------------------------------------------------------------------------
  const handleSubmit = async () => {
    try {
      const isEdit = mode === "edit";
      const id = formData.id;
      const url = isEdit ? PROJECTS_API.byId(id) : PROJECTS_API.base;
      const method = isEdit ? "PUT" : "POST";

      const currentUser = JSON.parse(localStorage.getItem("corelia_user") || "{}");

      const payload = {
        name: formData.name,
        code: formData.code || null,
        description: formData.description || null,
        location: formData.location || null,
        sector: formData.sector || null,
        status: formData.status || "Active",
        currency: formData.currency || "EUR",
        archived: formData.archived,
        userId: formData.userId ?? formData.ownerId ?? currentUser.id,
        accountId: formData.accountId ?? currentUser.accountId ?? null,
      };

      const res = await apiFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const msg = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          throw new Error(data?.message || JSON.stringify(data) || msg);
        } catch {
          throw new Error(msg);
        }
      }

      const data = await res.json();
      const project = data?.project ?? data;
      onSave(project);
      onClose();
    } catch (err) {
      console.error("Erreur :", err);
      alert(err.message);
    }
  };

  // --------------------------------------------------------------------------
  // 6. Fermeture du modal si non ouvert
  // --------------------------------------------------------------------------
  if (!isOpen) return null;

  // --------------------------------------------------------------------------
  // 7. Styles internes
  // --------------------------------------------------------------------------
  const labelStyle = { display: "block" };
  const fieldStyle = {
    width: "95%",
    fontSize: theme.form.field.fontSize,
    padding: `${theme.form.field.paddingV}px ${theme.form.field.paddingH}px`,
  };

  // Palette de couleurs spécifique pour le statut du projet
  const projectStatusColors = theme.statusColors?.projectStatus;


  // --------------------------------------------------------------------------
  // 8. Rendu JSX
  // --------------------------------------------------------------------------
  return (
    <ModalTemplate
      isOpen={isOpen}
      title={mode === "edit" ? "Editer le projet" : "Créer un projet"}
      onClose={onClose}
      onSubmit={handleSubmit}
      width="600px"
    >
      <p style={{ color: theme.colors.text.secondary, marginBottom: theme.spacing.xs, fontSize: theme.fontSizes.sm , marginBottom: "12px"}}>
              Gérez les informations du projet et son statut.
            </p>
      {/* -------------------------------------------------------------- */}
      {/* Champs de saisie principaux */}
      {/* -------------------------------------------------------------- */}
      <Input
        label={<span style={labelStyle}>Code</span>}
        value={formData.code}
        onChange={handleChange("code")}
        placeholder="exemple: 2022-CP-102"
        inputStyle={fieldStyle}
        width="100%"
      />

      <Input
        label={<span style={labelStyle}>Dénomination</span>}
        value={formData.name}
        onChange={handleChange("name")}
        placeholder="exemple: Palais des Congrès"
        inputStyle={fieldStyle}
        width="100%"
      />

      <TextArea
        label={<span style={labelStyle}>Description</span>}
        value={formData.description}
        onChange={handleChange("description")}
        placeholder="Description utile"
        inputStyle={fieldStyle}
        resize="none"
        rows={3}
        width="100%"
      />

      <Input
        label={<span style={labelStyle}>Localisation</span>}
        value={formData.location}
        onChange={handleChange("location")}
        placeholder="Ville ou pays"
        inputStyle={fieldStyle}
        width="63%"
      />

      <div style={{ width: "60%" }}>
        <DropdownList
          label={<span style={labelStyle}>Secteur</span>}
          value={formData.sector}
          onChange={(value) => setFormData((prev) => ({ ...prev, sector: value }))}
          options={[
            { value: "Building", label: "Bâtiment" },
            { value: "Infrastructure", label: "Infrastructure" },
            { value: "Industry", label: "Industrie" },
          ]}
          placeholder="Sélectionner un secteur..."
        />
      </div>

      

      {/* -------------------------------------------------------------- */}
      {/* Statut + Case Archivé */}
      {/* -------------------------------------------------------------- */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing.md,
          marginBottom: theme.spacing.xs,
        }}
      >
        {/* --- Dropdown Statut --- */}
        <div style={{ width: "35%" }}>
          <DropdownList
            label={<span style={labelStyle}>Statut</span>}
            value={formData.status}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, status: value }))
            }
            options={Object.entries(PROJECT_STATUS).map(([key, value]) => ({
              value,
              label: value,
            }))}
            placeholder="Sélectionner un statut..."
            colorCategory={projectStatusColors} // ✅ envoie l’objet directement
          />
        </div>

        {/* --- Checkbox Archivé (lecture seule, label dynamique) --- */}
        <Checkbox
          label={formData.archived ? "Close" : "Open"}
          checked={formData.archived}
          onChange={() => {}}
          disabled={true}
        />
      </div>
    </ModalTemplate>
  );
};

// ============================================================================
export default ProjectInfoManage;
